import React, { useEffect, useState, useRef } from 'react';
import { ContentPopup } from 'components/ContentPopup.jsx';
import { PostRequest } from 'components/common/index.js';

// stripeId names the subscription being upgraded. Eligibility is decided per subscription
// and the card renders this modal per subscription, so without it the server falls back to
// the customer's oldest subscription -- which for anyone holding more than one is not
// necessarily the one whose link they clicked.
const UpgradeToAnnualModal = ({ onClose, onSuccess, stripeId }) => {
  const [mode, setMode]       = useState('loading');
  const [preview, setPreview] = useState(null);
  const [error, setError]     = useState('');
  const [pending, setPending] = useState(false);
  const popupRef              = useRef();
  const nonceRef              = useRef(Math.random().toString(36).slice(2));

  const close = () => {
    if (popupRef.current) {
      popupRef.current.style.display = 'none';
    }
    if (onClose) {
      onClose();
    }
  };

  const fetchPreview = () => {
    PostRequest({
      url: '/charge/preview-upgrade-to-annual',
      data: { stripe_id: stripeId },
      callback: (data) => {
        setPreview(data);
        setMode('preview');
      },
      422: (data) => {
        setError((data && data.error) || t('payment.upgrade_failed'));
        setMode('error');
      }
    });
  };

  useEffect(() => {
    fetchPreview();
  }, []);

  const confirm = () => {
    setPending(true);
    PostRequest({
      url: '/charge/upgrade-to-annual',
      data: { nonce: nonceRef.current, stripe_id: stripeId },
      callback: (data) => {
        setPending(false);
        if (data && data.ok) {
          setMode('success');
        } else {
          setError((data && data.error) || t('payment.upgrade_failed'));
          setMode('error');
        }
      },
      422: (data) => {
        setPending(false);
        setError((data && data.error) || t('payment.upgrade_failed'));
        setMode('error');
      }
    });
  };

  const loadingContent = (
    <div className="upgrade-to-annual-modal upgrade-to-annual-modal-loading" role="status" aria-label={t('payment.upgrade_to_annual_loading_label')}>
      <div className="upgrade-to-annual-spinner" aria-hidden="true" />
    </div>
  );

  const previewContent = preview && (
    <div className="upgrade-to-annual-modal">
      <h3 className="upgrade-to-annual-modal-title">
        {t('payment.upgrade_to_annual_modal_title')}
      </h3>

      <div className="upgrade-to-annual-modal-breakdown">
        <div className="upgrade-to-annual-modal-line">
          <span>{t('payment.upgrade_to_annual_modal_annual_line')}</span>
          <span>{preview.annual_price_formatted}</span>
        </div>
        <div className="upgrade-to-annual-modal-line upgrade-to-annual-modal-credit">
          <span>{t('payment.upgrade_to_annual_modal_credit_line')}</span>
          <span>- {preview.proration_credit_formatted}</span>
        </div>
        <div className="upgrade-to-annual-modal-divider" />
        <div className="upgrade-to-annual-modal-line upgrade-to-annual-modal-total">
          <span>{t('payment.upgrade_to_annual_modal_charged_today')}</span>
          <span>{preview.charged_today_formatted}</span>
        </div>
      </div>

      <p className="upgrade-to-annual-modal-renewal">
        {t('payment.upgrade_to_annual_modal_renewal_line')
          .replace('%{date}', preview.renewal_date_formatted)
          .replace('%{amount}', preview.annual_price_formatted)}
      </p>

      <div className="upgrade-to-annual-modal-actions">
        <a className="btn upgrade-to-annual-modal-cancel" onClick={close} href="#">
          {t('payment.upgrade_to_annual_cancel_cta')}
        </a>
        <a
          className="btn subscription-card-action-primary"
          onClick={(event) => { event.preventDefault(); if (!pending) { confirm(); } }}
          href="#"
          aria-disabled={pending}
        >
          {pending ? t('sign_up.please_wait') : t('payment.upgrade_to_annual_confirm_cta')}
        </a>
      </div>
    </div>
  );

  const successContent = (
    <div className="upgrade-to-annual-modal upgrade-to-annual-modal-success">
      <h3>{t('payment.upgrade_to_annual_success_title')}</h3>
      <p>{t('payment.upgrade_to_annual_success_body')}</p>
      <div className="upgrade-to-annual-modal-actions">
        <a
          className="btn subscription-card-action-primary"
          onClick={(event) => { event.preventDefault(); if (onSuccess) { onSuccess(); } close(); }}
          href="#"
        >
          {t('payment.upgrade_to_annual_success_close_cta')}
        </a>
      </div>
    </div>
  );

  const errorContent = (
    <div className="upgrade-to-annual-modal upgrade-to-annual-modal-error">
      <h3>{t('payment.upgrade_to_annual_error_title')}</h3>
      <p>{error}</p>
      <div className="upgrade-to-annual-modal-actions">
        <a className="btn upgrade-to-annual-modal-cancel" onClick={close} href="#">
          {t('payment.upgrade_to_annual_cancel_cta')}
        </a>
      </div>
    </div>
  );

  let content;
  if (mode === 'loading') {
    content = loadingContent;
  } else if (mode === 'preview') {
    content = previewContent;
  } else if (mode === 'success') {
    content = successContent;
  } else {
    content = errorContent;
  }

  return (
    <ContentPopup
      myRef={popupRef}
      autoOpen={true}
      content={content}
      cancelButton={false}
      beforeClose={onClose}
      notWide={true}
    />
  );
};

export default UpgradeToAnnualModal;
